import { useTranslation } from 'react-i18next';

import { Button } from '@/components/ui/button';
import { recipientErrorStatuses } from '@/modules/whatsapp/constants';
import type { WhatsappValidationResult } from '@/shared/types/whatsapp';

interface Props {
  issue: WhatsappValidationResult;
  isConnecting?: boolean;
  onAction?: () => void;
}

const ACTION_LABEL_KEY: Record<NonNullable<WhatsappValidationResult['action']>, string> = {
  reauthorize: 'integrations.reauthorize',
  open_advanced_settings: 'integrations.whatsapp.go_to_advanced_settings',
};

const CATEGORY_LABEL_KEY: Record<WhatsappValidationResult['category'], string> = {
  whatsapp: 'integrations.whatsapp.validation.category.whatsapp',
  authorization: 'integrations.whatsapp.validation.category.authorization',
  rate_limits: 'integrations.whatsapp.validation.category.rate_limits',
  payment: 'integrations.whatsapp.validation.category.payment',
};

const MESSAGE_KEY_BY_CODE: Record<string, string> = {
  reauth_required: 'integrations.whatsapp.reauthorise.subtitle',
  encryption_mismatch: 'integrations.whatsapp.encryption_mismatch.title',
  whatsapp_account_unavailable: 'integrations.whatsapp.validation.account_unavailable',
  display_name_declined: 'integrations.whatsapp.display_name.declined',
  display_name_expired: 'integrations.whatsapp.display_name.expired',
};

const SOLUTION_KEY_BY_CODE: Record<string, string> = {
  encryption_mismatch: 'integrations.whatsapp.encryption_mismatch.solution',
  whatsapp_account_unavailable: 'integrations.whatsapp.data_fetch_error',
  display_name_declined: 'integrations.whatsapp.display_name.solution',
  display_name_expired: 'integrations.whatsapp.display_name.solution',
};

const META_CODE_PREFIX = 'meta:';

const lookupMetaTranslationKey = (code: string): string | undefined => {
  if (!code.startsWith(META_CODE_PREFIX)) {
    return undefined;
  }
  const num = code.slice(META_CODE_PREFIX.length);
  return recipientErrorStatuses[num as keyof typeof recipientErrorStatuses];
};

const messageTranslationKey = (code: string): string | undefined =>
  MESSAGE_KEY_BY_CODE[code] ?? lookupMetaTranslationKey(code);

export const HealthIssueItem = ({ issue, isConnecting, onAction }: Props) => {
  const { t } = useTranslation();

  const messageKey = messageTranslationKey(issue.code);
  const hasFriendlyMessage = !!messageKey;
  const message = messageKey ? t(messageKey) : issue.message;
  const solutionKey = SOLUTION_KEY_BY_CODE[issue.code];
  const solution = solutionKey ? t(solutionKey) : issue.solution;

  return (
    <li className="space-y-2 text-sm">
      {message && <p>{message}</p>}
      {solution && <p className="text-foreground-muted">{solution}</p>}
      <div className="flex items-center gap-2">
        <span className="inline-flex items-center rounded border border-stroke bg-bg-muted-50 px-1.5 py-0.5 text-xs font-medium text-foreground-muted">
          {t(CATEGORY_LABEL_KEY[issue.category])}
        </span>
        {/* Raw code is shown only for issues whose message/solution come from upstream
            (Meta / unknown codes). When we have a translated, human-readable message
            for the code, the raw token is noise. */}
        {!hasFriendlyMessage && (
          <span className="inline-flex items-center rounded border border-stroke bg-bg-muted-50 px-1.5 py-0.5 text-xs font-medium text-foreground-muted">
            {t('integrations.whatsapp.validation.code_label', { 0: issue.code })}
          </span>
        )}
        {issue.action && onAction && (
          <Button variant="outline-neutral" size="sm" onClick={onAction} isLoading={isConnecting}>
            {t(ACTION_LABEL_KEY[issue.action])}
          </Button>
        )}
      </div>
    </li>
  );
};
